import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationLinkComponent } from '../navigation-link/navigation-link.component';
import { NavigationConfig, NavigationEvent } from '../navigation-link/interfaces/navigation.interfaces';
import { SubRouteNavigationService, RouteNavigationData, RouteQueryParams, RouteNavigationEvent } from '../../services/sub-route-navigation.service';

/**
 * Route Display Mode
 */
export type RouteDisplayMode = 'code' | 'name' | 'both';

/**
 * Sub-Route specific navigation component
 * Uses NavigationLinkComponent internally and provides Sub-Route-specific functionality
 * Handles display modes, validation, and Sub-Route-specific navigation logic
 */
@Component({
  selector: 'app-sub-route-navigation',
  standalone: true,
  imports: [
    CommonModule,
    NavigationLinkComponent
  ],
  templateUrl: './sub-route-navigation.component.html',
  styleUrls: ['./sub-route-navigation.component.scss']
})
export class SubRouteNavigationComponent {
  /** Route data for navigation */
  @Input() route: RouteNavigationData;

  /** Display mode for the navigation link text */
  @Input() displayMode: RouteDisplayMode = 'code';

  /** Custom query parameters to include in navigation */
  @Input() customQueryParams: RouteQueryParams = {};

  /** Whether to open navigation in new tab */
  @Input() openInNewTab: boolean = true;

  /** Custom CSS classes for styling */
  @Input() customClass: string = '';

  /** Whether to show the icon */
  @Input() showIcon: boolean = true;

  /** Position of the icon */
  @Input() iconPosition: 'left' | 'right' = 'right';

  /** Custom icon (defaults to route icon) */
  @Input() icon: string = 'heroicons_outline:rocket-launch';

  /** Whether to show the text */
  @Input() showText: boolean = true;

  /** Which parts are clickable */
  @Input() interactionMode: 'both' | 'text-only' | 'icon-only' = 'both';

  /** Custom tooltip text (if empty, uses service default) */
  @Input() customTooltipText?: string;

  /** Event emitted when navigation occurs */
  @Output() routeNavigate = new EventEmitter<RouteNavigationEvent>();

  constructor(private subRouteNavService: SubRouteNavigationService) {}

  /**
   * Check if the route is navigable
   */
  get isNavigable(): boolean {
    return this.subRouteNavService.isRouteNavigable(this.route);
  }

  /**
   * Get display text based on display mode and available data
   */
  get displayText(): string {
    if (!this.route) return '-';

    const code = this.route.externalKeys?.code || this.route.routeCode || this.route.code;
    const name = this.route.routeName || this.route.name;

    switch (this.displayMode) {
      case 'code':
        return code || '-';

      case 'name':
        return name || '-';

      case 'both':
        if (code && name) {
          return `${code} - ${name}`;
        }
        return code || name || '-';

      default:
        return code || '-';
    }
  }

  /**
   * Code portion for stacked layout (bold, first line).
   * Non-empty when route has a code and displayMode === 'both'.
   */
  get displayCode(): string {
    if (this.displayMode !== 'both') return '';
    if (!this.route) return '';
    return this.route.externalKeys?.code || this.route.routeCode || this.route.code || '';
  }

  /**
   * Name portion for stacked layout (muted, second line).
   * Non-empty when route has a name and displayMode === 'both'.
   */
  get displayName(): string {
    if (this.displayMode !== 'both') return '';
    return this.route?.routeName || this.route?.name || '';
  }

  /**
   * Get tooltip text for the navigation link
   */
  get tooltipText(): string {
    if (!this.isNavigable) return '';

    return this.customTooltipText || this.subRouteNavService.getNavigationDescription();
  }

  /**
   * Get navigation configuration for the core NavigationLinkComponent
   */
  get navigationConfig(): NavigationConfig<RouteNavigationData, RouteQueryParams> {
    return {
      data: this.route,
      url: ['master', 'routing', 'sub-route'],
      queryParams: this.customQueryParams,
      openInNewTab: this.openInNewTab
    };
  }

  /**
   * Handle navigation event from NavigationLinkComponent
   */
  handleNavigation(navigationEvent: NavigationEvent<RouteNavigationData, RouteQueryParams>): void {
    if (!this.isNavigable) return;

    // Create route specific navigation event
    const routeNavigationEvent: RouteNavigationEvent = {
      data: navigationEvent.config.data,
      queryParams: navigationEvent.config.queryParams || {},
      timestamp: navigationEvent.timestamp,
      source: 'general'
    };

    // Emit route navigation event
    this.routeNavigate.emit(routeNavigationEvent);

    // Execute navigation through service
    this.subRouteNavService.navigateToRouteDetail(
      navigationEvent.config.data,
      navigationEvent.config.queryParams,
      navigationEvent.config.openInNewTab
    );
  }

  /**
   * Get display text with fallback handling
   */
  private getDisplayTextWithFallback(): string {
    const text = this.displayText;
    return text === '-' ? '' : text;
  }

  /**
   * Check if route has valid display data
   */
  hasValidDisplayData(): boolean {
    return this.getDisplayTextWithFallback().length > 0;
  }

  /**
   * Get CSS classes for the container
   */
  getContainerClasses(): string {
    const baseClasses = 'sub-route-navigation';
    const customClasses = this.customClass || '';
    const navigableClasses = this.isNavigable ? 'navigable' : 'non-navigable';

    return [baseClasses, navigableClasses, customClasses]
      .filter(cls => cls.length > 0)
      .join(' ');
  }
}
